import { useNavigate } from "@tanstack/react-router";
import { LockKeyhole } from "lucide-react";
import { Badge, Button } from "@studio";
import { errorMessage } from "../lib/api";
import { useAuth } from "../lib/auth";
import { native } from "../lib/native";
import { useCustomerStatus, useSummary } from "../lib/queries";
import type { Me } from "../lib/types";
import { ConsoleShell } from "../console/Shell";
import { Brand, ErrorBox, InfoRow, Loading, Panel } from "../console/ui";
import { useLocalStatus } from "./machines";
import { SeoAnalyticsScreen } from "./analytics";
import { NativeSettingsView, ReadOnlyMachineView, SystemStatusView } from "./surface";

/**
 * 데스크톱 앱 (Electron, 머신 계정) — 시안 d074369 `meta-crew-electron--*`.
 * 같은 웹 콘솔을 메뉴만 걸러 띄운다: 현황 · 머신 · 분석 · 이 PC 설정(네이티브). 작업 내용은 없다.
 * 엔진은 화면과 상관없이 작업을 처리하고 상태를 코어에 보낸다.
 */
export function BridgePage({ me, screen }: { me: Me; screen: string }) {
  const summary = useSummary(me.customerId ?? "");
  const status = useCustomerStatus(me.customerId ?? "");
  const nativeAvailable = !!native();
  const local = useLocalStatus(nativeAvailable);
  const navigate = useNavigate();
  // 현황의 머신 카드·연결 구조도는 공통 화면 이름 "machines" 로 부른다 → 데스크톱은 "machine" (2026-10-20 접근 권한 없음 오류)
  const go = (s: string) => void navigate({ to: "/bridge/$screen", params: { screen: s === "machines" ? "machine" : s } });
  if (summary.isLoading) return <Loading />;
  if (summary.error || !summary.data) return <ErrorBox error={errorMessage(summary.error)} onRetry={() => void summary.refetch()} />;
  const c = summary.data;
  const machineName = status.data?.machines[0]?.label ?? "이 PC";
  // 서비스 정지 2단계(잠금) 이상·해지: 이 화면만 보이고 아무 조작도 못 한다 (2026-10-20 사용자 결정).
  // 엔진은 코어가 작업을 취소·배정 중지하므로 일하지 않는다. 해지는 토큰이 폐기돼 보통 페어링 화면으로 간다.
  const stopped = c.status === "suspended" && c.sanctionLevel >= 2;
  if (stopped || c.sanctionLevel >= 3 || c.status === "terminated") {
    return (
      <div className="mcc-fallback">
        <Brand bridge />
        <div className="mcc-fallback-card">
          <div className="mcc-avatar">
            <LockKeyhole />
          </div>
          <Badge tone="coral">{c.status === "terminated" ? "서비스 해지" : "서비스 정지 중"}</Badge>
          <h1>{c.status === "terminated" ? "서비스가 해지되었습니다" : "서비스가 정지되었습니다"}</h1>
          <p>이 PC 에서는 작업이 실행되지 않습니다. 운영 담당자에게 문의해 주세요. 정지가 풀리면 자동으로 다시 열립니다.</p>
          <Panel title="운영 안내">
            <InfoRow title="담당자에게 연결 상태를 전달해 주세요" description={c.statusReason ?? (c.support ? `문의: ${c.support}` : "새 작업과 로컬 설정은 잠겨 있습니다.")} />
          </Panel>
        </div>
      </div>
    );
  }
  return (
    <ConsoleShell me={me} scope="desktop" screen={screen} customer={c} machineName={machineName} local={local} nativeAvailable={nativeAvailable}>
      {screen === "overview" ? (
        <SystemStatusView customerId={c.id} customerName={c.name} desktop local={local} onNavigate={go} />
      ) : screen === "machine" ? (
        me.machineId ? <ReadOnlyMachineView customerId={c.id} machineId={me.machineId} local={local} /> : <ErrorBox error="머신 정보를 찾을 수 없습니다." />
      ) : screen === "marketing" ? (
        <SeoAnalyticsScreen customerId={c.id} />
      ) : screen === "settings" ? (
        <NativeSettingsView local={local} onUnpaired={() => undefined /* Electron 이 폴백(페어링) 화면으로 바꾼다 */} />
      ) : null}
    </ConsoleShell>
  );
}

export function BridgeUnavailable() {
  const err = useAuth((s) => s.bridgeError);
  return (
    <div className="mcc-fallback">
      <Brand bridge />
      <div className="mcc-fallback-card">
        <Badge tone="coral">{err === "offline" ? "서버 연결 중" : "연결 필요"}</Badge>
        <h1>{err === "offline" ? "서버에 연결하고 있습니다" : "이 PC를 다시 연결해야 합니다"}</h1>
        <p>{err === "offline" ? "네트워크가 복구되면 자동으로 다시 시도합니다. 작업 처리와 이벤트 보관은 이 PC에서 계속됩니다." : "관리자에게 새 8자리 코드를 받아 입력하세요."}</p>
        <Button onClick={() => window.location.reload()}>다시 시도</Button>
      </div>
    </div>
  );
}
